import React, { useContext, useEffect, useState } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Grid,
} from "@mui/material";
import { Formik } from "formik";
import Commonmodel from "../../Commonmodel/Commonmodel";
import { addDoctorSchema } from "../../../schema/addDoctorSchema";
import { getAddDoctorInitialValues } from "../../../schema/addDoctorInitialValues";
import { CommonContext } from "../../../context/CommonContext";
import { apisPath } from "../../../utils/path";
import { authHttp } from "../../../services/commonHttpService";

const AddDoctorModal = (props) => {
  const { open, onClose, selectedView, getDoctorData, getDoctorsCardDetails } = props;
  const { setIsLoading, setSnackState, getMasterData } = useContext(CommonContext);
  const [statesList, setStatesList] = useState([]);

  const isDoctor = selectedView === "doctors";
  const modalTitle = isDoctor ? "Add Doctor Details" : "Add Nurse Details";
  const buttonText = isDoctor ? "Add Doctor" : "Add Nurse";

  // Doctor specialization options
  const doctorSpecializationOptions = [
    "Medical oncologist",
    "Surgical oncologist",
    "Radiation oncologist",
    "Pathologist",
    "Radiologist",
  ];

  // Nurse specialization options
  // const nurseSpecializationOptions = [
  //   "General",
  //   "Oncology",
  //   "Chemotherapy",
  //   "Radiation",
  //   "Pediatric Oncology",
  //   "Palliative Care",
  // ];

  // // Get specialization options based on selected view
  // const specializationOptions = isDoctor
  //   ? doctorSpecializationOptions
  //   : nurseSpecializationOptions;

  // Training status options
  const trainingStatusOptions = [
    { label: "No", value: 0 },
    { label: "Yes", value: 1 },
  ];

  // Fetch state master data on component mount using getMasterData
  useEffect(() => {
    if (open && isDoctor) {
      loadStates();
    }
  }, [open, isDoctor]);

  const loadStates = async () => {
    const res = await getMasterData(apisPath?.masters?.stateMaster, "states");
    if (res) {
      setStatesList(res);
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsLoading(true);

      const payload = {
        doctor_name: values?.doctor_name,
        training_status: parseInt(values?.training_status),
        phone_number: values?.phone_number,
        email: values?.email,
        type: isDoctor ? "Doctor" : "Nurse",
        is_active: 1,
      };

      // Add doctor-specific fields
      if (isDoctor) {
        payload.designation = values?.designation || null;
        payload.hpr_id = values?.hpr_id || null;
        payload.reg_number = values?.reg_number || null;
        payload.reg_year = values?.reg_year ? parseInt(values?.reg_year) : null;
        payload.state_lgd = values?.state_lgd ? parseInt(values?.state_lgd) : null;
        payload.insource_outsource = values?.insource_outsource;
        payload.visiting_from =
          values?.insource_outsource === "Outsource" ? values?.visiting_from : null;
      } else {
        // For nurses, send empty strings
        payload.designation = "";
        payload.hpr_id = "";
        payload.reg_number = "";
        payload.reg_year = null;
        payload.state_lgd = 0;
        payload.insource_outsource = "";
        payload.visiting_from = "";
      }

      const response = await authHttp.post(apisPath?.createDoctorDetails, payload);

      if (response?.data?.success) {
        setSnackState({
          open: true,
          message:
            response?.data?.message ||
            `${isDoctor ? "Doctor" : "Nurse"} added successfully`,
          severity: "success",
        });

        // Refresh data
        getDoctorData();
        getDoctorsCardDetails();

        // Reset form and close modal
        resetForm();
        onClose();
      }
    } catch (error) {
      setSnackState({
        open: true,
        message:
          error?.response?.data?.message ||
          `Failed to add ${isDoctor ? "doctor" : "nurse"}`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Commonmodel
      open={open}
      onClose={onClose}
      title={modalTitle}
      showActions={false}
    >
      <Formik
        initialValues={getAddDoctorInitialValues()}
        validationSchema={addDoctorSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* Name */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Name"
                  name="doctor_name"
                  value={values?.doctor_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.doctor_name && !!errors.doctor_name}
                  helperText={touched.doctor_name && errors.doctor_name}
                  variant="standard"
                  required
                />
              </Grid>

              {/* Specialization - Optional - Only for Doctors */}
              {isDoctor && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="Specialization"
                    name="designation"
                    id="designation"
                    value={values?.designation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.designation && !!errors.designation}
                    helperText={touched.designation && errors.designation}
                    variant="standard"
                  >
                    <MenuItem value="">Select Specialization</MenuItem>
                    {doctorSpecializationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Training Status */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Training Completed"
                  name="training_status"
                  value={values?.training_status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.training_status && !!errors.training_status}
                  helperText={touched.training_status && errors.training_status}
                  variant="standard"
                  required
                >
                  <MenuItem value="">Select Status</MenuItem>
                  {trainingStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Mobile No. */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Mobile No."
                  name="phone_number"
                  value={values?.phone_number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                    setFieldValue("phone_number", value);
                  }}
                  onBlur={handleBlur}
                  error={touched.phone_number && !!errors.phone_number}
                  helperText={touched.phone_number && errors.phone_number}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 10 }
                    }
                  }}
                  variant="standard"
                  required
                />
              </Grid>

              {/* Email ID */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Email ID"
                  name="email"
                  type="email"
                  value={values?.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  variant="standard"
                  required
                />
              </Grid>

              {/* Doctor-only fields */}
              {isDoctor && (
                <>
                  {/* HPR ID - Optional */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="HPR ID"
                      name="hpr_id"
                      value={values?.hpr_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.hpr_id && !!errors.hpr_id}
                      helperText={touched.hpr_id && errors.hpr_id}
                      variant="standard"
                    />
                  </Grid>

                  {/* Registration No. - Optional */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Registration No."
                      name="reg_number"
                      value={values?.reg_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.reg_number && !!errors.reg_number}
                      helperText={touched.reg_number && errors.reg_number}
                      variant="standard"
                    />
                  </Grid>

                  {/* Registration Year - Optional */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Registration Year"
                      name="reg_year"
                      value={values?.reg_year}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
                        setFieldValue("reg_year", value);
                      }}
                      onBlur={handleBlur}
                      error={touched.reg_year && !!errors.reg_year}
                      helperText={touched.reg_year && errors.reg_year}
                      slotProps={{
                        input: {
                          inputProps: { maxLength: 4 }
                        }
                      }}
                      variant="standard"
                    />
                  </Grid>

                  {/* Registration State - Optional */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      select
                      label="Registration State"
                      name="state_lgd"
                      value={values?.state_lgd}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.state_lgd && !!errors.state_lgd}
                      helperText={touched.state_lgd && errors.state_lgd}
                      variant="standard"
                    >
                      <MenuItem value="">Select State</MenuItem>
                      {statesList.map((state) => (
                        <MenuItem key={state.state_lgd} value={state.state_lgd}>
                          {state.state_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Posting - Optional */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl
                      component="fieldset"
                      error={touched.insource_outsource && !!errors.insource_outsource}
                    >
                      <FormLabel
                        component="legend"
                        sx={{
                          fontSize: "12px",
                          color: "rgba(0, 0, 0, 0.6)",
                          marginBottom: "8px",
                        }}
                      >
                        Posting
                      </FormLabel>
                      <RadioGroup
                        row
                        name="insource_outsource"
                        value={values?.insource_outsource}
                        onChange={(e) => {
                          setFieldValue("insource_outsource", e.target.value);
                          if (e.target.value === "Insource") {
                            setFieldValue("visiting_from", "");
                          }
                        }}
                      >
                        <FormControlLabel
                          value="Insource"
                          control={<Radio />}
                          label="Inhouse"
                        />
                        <FormControlLabel
                          value="Outsource"
                          control={<Radio />}
                          label="Visiting"
                        />
                      </RadioGroup>
                      {touched.insource_outsource && errors.insource_outsource && (
                        <FormHelperText>{errors.insource_outsource}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Visiting from - Optional */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Visiting from"
                      name="visiting_from"
                      value={values?.visiting_from}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={values?.insource_outsource === "Insource"}
                      error={touched.visiting_from && !!errors.visiting_from}
                      helperText={touched.visiting_from && errors.visiting_from}
                      variant="standard"
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{
                marginTop: "32px",
                backgroundColor: "#5B3FDB",
                color: "#fff",
                padding: "12px",
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#4A32B3",
                },
                "&:disabled": {
                  backgroundColor: "#9E9E9E",
                },
              }}
            >
              {buttonText}
            </Button>
          </form>
        )}
      </Formik>
    </Commonmodel>
  );
};

export default AddDoctorModal;